/**
 * Firebase Global Presence module.
 * Handles global monitoring of all admin sessions across platforms.
 *
 * @module firebase/presence/global
 */

import { ref, get, update, onValue, serverTimestamp } from "firebase/database";
import { db } from "../config";
import { SESSION_TIMING, parseFirebaseTimestamp, clearTimers } from "../utils";
import { recalculateAdminGlobalStatus } from "./core";

const globalMonitorTimers = {};
let globalMonitoringActive = false;
let globalMonitorCleanup = null;

/**
 * Clears timers for global session monitoring
 *
 * @param {string} timerKey - The key for the timer
 */
export const clearGlobalMonitorTimer = (timerKey) => {
  const timer = globalMonitorTimers[timerKey];
  if (timer) {
    clearTimers(timer.awayTimer, timer.offlineTimer);
    delete globalMonitorTimers[timerKey];
  }
};

/**
 * Sets up timers for global session monitoring
 *
 * @param {string} adminId - The ID of the admin
 * @param {string} sessionId - The ID of the session
 * @param {string} monitorKey - The key for the monitor
 * @param {number|Object} lastActiveTimestamp - Timestamp of the last activity
 * @param {string} currentStatus - The current status of the session
 * @param {string} sessionType - The type of session ('web' or 'mobile')
 */
const setGlobalSessionTimers = (
  adminId,
  sessionId,
  monitorKey,
  lastActiveTimestamp,
  currentStatus,
  sessionType
) => {
  const sessionRef = ref(
    db,
    `admins/${adminId}/sessions/${sessionType}/${sessionId}`
  );

  let now = Date.now();
  let lastActiveTime = parseFirebaseTimestamp(lastActiveTimestamp);
  const inactiveTime = now - lastActiveTime;
  const timeUntilAway = Math.max(0, SESSION_TIMING.AWAY_TIMEOUT - inactiveTime);
  const timeUntilOffline = Math.max(
    0,
    SESSION_TIMING.OFFLINE_TIMEOUT - inactiveTime
  );

  globalMonitorTimers[monitorKey] = {
    awayTimer: null,
    offlineTimer: null,
    sessionType: sessionType,
    adminId: adminId,
    sessionId: sessionId,
  };

  if (sessionType === "mobile") {
    get(sessionRef)
      .then((snapshot) => {
        if (snapshot.exists() && snapshot.val().appState === "active") {
          clearGlobalMonitorTimer(monitorKey);
          return;
        }
        setupStatusTimers();
      })
      .catch((error) => {
        console.error("Error checking mobile session:", error);
        setupStatusTimers();
      });
  } else {
    setupStatusTimers();
  }

  /**
   * Sets up timers for status changes
   */
  function setupStatusTimers() {
    if (timeUntilAway <= 0 && currentStatus === "online") {
      update(sessionRef, {
        status: "away",
        lastStatusChange: serverTimestamp(),
        _updatedBy: "globalMonitor",
      })
        .then(() => {
          recalculateAdminGlobalStatus(adminId);
        })
        .catch((error) => {
          console.error("Error updating to away status:", error);
        });
    } else if (timeUntilAway > 0 && currentStatus === "online") {
      globalMonitorTimers[monitorKey].awayTimer = setTimeout(() => {
        get(sessionRef)
          .then((snapshot) => {
            if (!snapshot.exists()) return;

            const current = snapshot.val();
            if (
              (sessionType === "web" && current.status === "online") ||
              (sessionType === "mobile" &&
                current.appState !== "active" &&
                current.status === "online")
            ) {
              update(sessionRef, {
                status: "away",
                lastStatusChange: serverTimestamp(),
                _updatedBy: "globalMonitor",
              })
                .then(() => {
                  recalculateAdminGlobalStatus(adminId);
                })
                .catch((error) => {
                  console.error(
                    "Error updating to away status in timer:",
                    error
                  );
                });
            }
          })
          .catch((error) => {
            console.error("Error checking session for away status:", error);
          });
      }, timeUntilAway);
    }

    if (timeUntilOffline <= 0 && currentStatus !== "offline") {
      update(sessionRef, {
        status: "offline",
        lastStatusChange: serverTimestamp(),
        _updatedBy: "globalMonitor",
      })
        .then(() => {
          recalculateAdminGlobalStatus(adminId);
        })
        .catch((error) => {
          console.error("Error updating to offline status:", error);
        });
    } else if (timeUntilOffline > 0 && currentStatus !== "offline") {
      globalMonitorTimers[monitorKey].offlineTimer = setTimeout(() => {
        get(sessionRef)
          .then((snapshot) => {
            if (!snapshot.exists()) return;

            const current = snapshot.val();
            if (
              (sessionType === "web" && current.status !== "offline") ||
              (sessionType === "mobile" &&
                current.appState !== "active" &&
                current.status !== "offline")
            ) {
              update(sessionRef, {
                status: "offline",
                lastStatusChange: serverTimestamp(),
                _updatedBy: "globalMonitor",
              })
                .then(() => {
                  recalculateAdminGlobalStatus(adminId);
                })
                .catch((error) => {
                  console.error(
                    "Error updating to offline status in timer:",
                    error
                  );
                });
            }
          })
          .catch((error) => {
            console.error("Error checking session for offline status:", error);
          });
      }, timeUntilOffline);
    }
  }
};

/**
 * Handles web session updates for global monitoring
 *
 * @param {string} adminId - The ID of the admin
 * @param {string} sessionId - The ID of the web session
 * @param {Object} sessionData - The session data
 */
const handleGlobalWebSession = (adminId, sessionId, sessionData) => {
  if (!sessionData) return;

  const monitorKey = `global_web_${adminId}_${sessionId}`;
  clearGlobalMonitorTimer(monitorKey);

  setGlobalSessionTimers(
    adminId,
    sessionId,
    monitorKey,
    sessionData.lastActive,
    sessionData.status,
    "web"
  );
};

/**
 * Handles mobile session updates for global monitoring
 *
 * @param {string} adminId - The ID of the admin
 * @param {string} sessionId - The ID of the mobile session
 * @param {Object} sessionData - The session data
 */
const handleGlobalMobileSession = (adminId, sessionId, sessionData) => {
  if (!sessionData) return;

  const monitorKey = `global_mobile_${adminId}_${sessionId}`;
  clearGlobalMonitorTimer(monitorKey);

  if (sessionData.appState !== "active") {
    setGlobalSessionTimers(
      adminId,
      sessionId,
      monitorKey,
      sessionData.lastActive,
      sessionData.status,
      "mobile"
    );
  }
};

/**
 * Processes web sessions for an admin
 *
 * @param {string} adminId - The ID of the admin
 * @param {Object} webSessions - Web sessions data
 */
const processAdminWebSessions = (adminId, webSessions) => {
  if (!webSessions || Object.keys(webSessions).length === 0) return;

  Object.entries(webSessions).forEach(([sessionId, sessionData]) => {
    if (sessionId === window.adminConnectionId) return;
    handleGlobalWebSession(adminId, sessionId, sessionData);
  });
};

/**
 * Processes mobile sessions for an admin
 *
 * @param {string} adminId - The ID of the admin
 * @param {Object} mobileSessions - Mobile sessions data
 */
const processAdminMobileSessions = (adminId, mobileSessions) => {
  if (!mobileSessions || Object.keys(mobileSessions).length === 0) return;

  Object.entries(mobileSessions).forEach(([sessionId, sessionData]) => {
    handleGlobalMobileSession(adminId, sessionId, sessionData);
  });
};

/**
 * Monitors all admin sessions across platforms
 *
 * @returns {Function} Cleanup function
 */
export const monitorAllAdminSessions = () => {
  if (globalMonitoringActive) {
    return globalMonitorCleanup;
  }

  const adminsRef = ref(db, "admins");
  const heartbeatInterval = setInterval(() => {}, 60000);

  const unsubscribe = onValue(adminsRef, (snapshot) => {
    const admins = snapshot.val() || {};

    Object.entries(admins).forEach(([adminId, adminData]) => {
      processAdminWebSessions(adminId, adminData.sessions?.web || {});
      processAdminMobileSessions(adminId, adminData.sessions?.mobile || {});
    });
  });

  globalMonitoringActive = true;

  globalMonitorCleanup = () => {
    unsubscribe();
    clearInterval(heartbeatInterval);
    globalMonitoringActive = false;
  };

  return globalMonitorCleanup;
};

/**
 * Stops global monitoring and cleans up resources
 */
export const stopGlobalMonitoring = () => {
  if (globalMonitorCleanup && typeof globalMonitorCleanup === "function") {
    Object.entries(globalMonitorTimers).forEach(([timerKey]) => {
      clearGlobalMonitorTimer(timerKey);
    });

    globalMonitorCleanup();
  }
};

if (typeof window !== "undefined") {
  window.addEventListener("beforeunload", () => {
    const timersToClear = Object.keys(globalMonitorTimers).filter((key) =>
      key.includes("global_")
    );
    timersToClear.forEach((key) => clearGlobalMonitorTimer(key));
  });

  (() => {
    const initGlobalMonitoring = () => {
      if (!globalMonitoringActive) {
        monitorAllAdminSessions();
      }
    };

    initGlobalMonitoring();

    const connectedRef = ref(db, ".info/connected");
    onValue(connectedRef, (snapshot) => {
      if (snapshot.val() === true) {
        initGlobalMonitoring();
      }
    });

    setInterval(initGlobalMonitoring, 5 * 60 * 1000);
  })();
}
